import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { ICartItem } from '@/entities/cart/model/types';

type CartActions = {
  ADD_TO_CART: (payload: ICartItem & { key?: string }) => void;
  UPDATE_CART: (payload: ICartItem[]) => void;
  SET_QUANTITY: (payload: { key: string; quantity: number }) => void;
  INCREMENT: (key: string) => void;
  DECREMENT: (key: string) => void;
  REMOVE: (key: string) => void;
  UPDATE_MODIFIERS: (payload: {
    key: string;
    data: ICartItem['order_modifiers'];
  }) => void;
  STOP: (productId: string) => void;
  EXCLUDE: (productId: string) => void;
  EXCLUDE_STOP: () => void;
  CLEAR: () => void;
  ADD_COMMENT: (payload: { key: string; comment: string }) => void;
  DELETE_COMMENT: (key: string) => void;
};

type CartState = { cart: ICartItem[] } & CartActions;

export const useCartStore = create<CartState, [['zustand/persist', CartState]]>(
  persist(
    (set) => ({
      cart: [],
      ADD_TO_CART: (payload) =>
        set((state) => {
          const cart = [...state.cart];
          const quantity = payload.quantity ? payload.quantity : 1;

          const isSimpleWithoutModifiers =
            payload.type === 'simple' && payload.order_modifiers.length === 0;

          if (isSimpleWithoutModifiers) {
            const existingItem = cart.find(
              (item) => item.product_id === payload.product_id
            );

            if (existingItem) {
              if (!existingItem.default_product) {
                existingItem.quantity += 1;
              }
              return { cart: [...cart] };
            }
          }

          cart.push({
            ...payload,
            quantity,
            key: payload.key || uuidv4(),
          });
          return { cart };
        }),
      UPDATE_CART: (payload) =>
        set(() => ({
          cart: payload,
        })),
      SET_QUANTITY: (payload) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.key === payload.key
              ? { ...item, quantity: payload.quantity }
              : item
          ),
        })),
      INCREMENT: (key) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.key === key ? { ...item, quantity: item.quantity + 1 } : item
          ),
        })),
      DECREMENT: (key) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.key === key
              ? {
                  ...item,
                  quantity: item.quantity === 1 ? 1 : item.quantity - 1,
                }
              : item
          ),
        })),
      REMOVE: (key) =>
        set((state) => ({
          cart: state.cart.filter((item) => item.key !== key),
        })),
      UPDATE_MODIFIERS: (payload) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.key === payload.key
              ? { ...item, order_modifiers: payload.data }
              : item
          ),
        })),
      STOP: (productId) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.product_id === productId ? { ...item, in_stop: true } : item
          ),
        })),
      EXCLUDE: (productId) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.product_id === productId && item.in_stop
              ? { ...item, in_stop: false }
              : item
          ),
        })),
      EXCLUDE_STOP: () =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.in_stop ? { ...item, in_stop: false } : item
          ),
        })),
      CLEAR: () =>
        set(() => ({
          cart: [],
        })),
      ADD_COMMENT: (payload) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.key === payload.key
              ? { ...item, comment: payload.comment }
              : item
          ),
        })),
      DELETE_COMMENT: (key) =>
        set((state) => ({
          cart: state.cart.map((item) =>
            item.key === key ? { ...item, comment: '' } : item
          ),
        })),
    }),
    {
      name: 'cart-storage', // localStorage key
    }
  )
);
